/**
 * ResetPasswordDialog — password reset modal for user profile.
 * Validates: 6+ chars, no "123456" for high-authority users.
 */

import { useState, useCallback, useEffect } from 'react';

const ResetPasswordDialog = ({ userName, isHighAuthority, onClose, onConfirm, saving }) => {
    const [password, setPassword] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        setPassword('');
        setConfirmPw('');
        setError('');
    }, []);

    const validate = useCallback(() => {
        if (password.length < 6) return 'Password must be at least 6 characters.';
        if (isHighAuthority && password === '123456') return 'This password is not allowed for admin users.';
        if (password !== confirmPw) return 'Passwords do not match.';
        return '';
    }, [password, confirmPw, isHighAuthority]);

    const handleSubmit = useCallback(() => {
        const err = validate();
        if (err) { setError(err); return; }
        onConfirm(password);
    }, [password, validate, onConfirm]);

    return (
        <div className="pp-dialog-backdrop" onClick={onClose}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="pp-dialog__header" style={{ padding: 'var(--space-4) var(--space-5)', borderBottom: '1px solid var(--color-border)' }}>
                    <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-heading)' }}>
                        Reset Password
                    </h3>
                </div>
                <div className="pp-dialog__body" style={{ padding: 'var(--space-5)' }}>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                        Set a new password for <strong>{userName}</strong>.
                    </p>

                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">New Password <span className="dp-drawer__required">*</span></label>
                        <input type="password" className="dp-drawer__input" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} minLength={6} autoFocus />
                    </div>

                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">Confirm Password <span className="dp-drawer__required">*</span></label>
                        <input type="password" className="dp-drawer__input" value={confirmPw} onChange={(e) => { setConfirmPw(e.target.value); setError(''); }} minLength={6} />
                    </div>

                    {error ? (
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--space-2)' }}>{error}</p>
                    ) : null}
                </div>
                <div className="pp-dialog__footer" style={{ padding: 'var(--space-4) var(--space-5)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
                    <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="dp-btn dp-btn--primary" onClick={handleSubmit} disabled={saving || !password || !confirmPw}>
                        {saving ? 'Resetting\u2026' : 'Reset Password'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordDialog;
